<template>
    <a-modal
        :visible="visible"
        width="1200px"
        :footer="null"
        :closable="false"
        :destroy-on-close="true"
        centered
        wrap-class-name="dpm-wrap"
        @cancel="$emit('close')"
    >
        <div class="dpm-modal">
            <div class="dpm-head">
                <div>
                    <h3>{{ title }}</h3>
                    <div class="dpm-sub">
                        {{ requestLabel || (requestId
                            ? "Fornecedores que ofertam ou vendem os produtos desta cotação."
                            : "Fornecedores que ofertam ou vendem os seus produtos e ainda não estão na sua base.") }}
                    </div>
                </div>
                <a-tooltip title="Fechar">
                    <a-button class="dpm-x" @click="$emit('close')">
                        <span class="material-icons-outlined">close</span>
                    </a-button>
                </a-tooltip>
            </div>

            <a-form layout="vertical" class="dpm-tools" @submit.prevent="filtrar">
                <a-row :gutter="8">
                    <a-col :xs="24" :md="12" :xl="5">
                        <a-form-item label="Buscar por nome">
                            <a-input
                                v-model="filters.search"
                                placeholder="Nome ou CNPJ"
                                allow-clear
                                @pressEnter="filtrar"
                            />
                        </a-form-item>
                    </a-col>
                    <a-col :xs="24" :md="6" :xl="3">
                        <a-form-item label="Estado">
                            <a-select
                                v-model="filters.state"
                                show-search
                                allow-clear
                                placeholder="Todos"
                                class="fixed-height-select"
                                :options="states"
                                :filter-option="filterOption"
                                :dropdown-match-select-width="false"
                            />
                        </a-form-item>
                    </a-col>
                    <a-col :xs="24" :md="6" :xl="3">
                        <a-form-item label="Cidade">
                            <a-input v-model="filters.city" placeholder="Cidade" allow-clear @pressEnter="filtrar" />
                        </a-form-item>
                    </a-col>
                    <a-col :xs="24" :md="12" :xl="4">
                        <a-form-item label="Categorias">
                            <a-select
                                v-model="filters.categories"
                                mode="multiple"
                                allow-clear
                                placeholder="Todas"
                                class="fixed-height-select"
                                :max-tag-count="1"
                                :options="categories"
                                :filter-option="filterOption"
                            />
                        </a-form-item>
                    </a-col>
                    <a-col :xs="24" :md="12" :xl="5">
                        <a-form-item label="Produtos">
                            <a-select
                                v-model="filters.products"
                                mode="multiple"
                                label-in-value
                                allow-clear
                                placeholder="Todos"
                                class="fixed-height-select"
                                :max-tag-count="1"
                                :filter-option="false"
                                :not-found-content="searchingProducts ? 'Buscando...' : 'Digite ao menos 2 letras'"
                                @search="searchProducts"
                            >
                                <a-select-option v-for="p in productOptions" :key="String(p.pro_id)">
                                    {{ p.pro_descricao }}
                                </a-select-option>
                            </a-select>
                        </a-form-item>
                    </a-col>
                    <a-col flex="auto" class="actions-col">
                        <a-form-item label="\u200E" class="item-button">
                            <div class="actions-right">
                                <a-button type="danger" @click="limparFiltros">
                                    <i class="fa-solid fa-filter-circle-xmark"></i>&nbsp;Limpar
                                </a-button>
                                <a-button type="default" icon="search" html-type="submit">Filtrar</a-button>
                            </div>
                        </a-form-item>
                    </a-col>
                </a-row>
            </a-form>

            <a-alert
                v-if="feedback"
                class="dpm-alert"
                :type="feedback.type"
                :message="feedback.message"
                :description="feedback.description"
                show-icon
                closable
                @close="feedback = null"
            />

            <div class="dpm-body">
                <a-table
                    size="small"
                    class="dpm-table"
                    :columns="columns"
                    :data-source="providers"
                    row-key="id"
                    :loading="loading"
                    :pagination="pagination"
                    :row-selection="canLink ? rowSelection : null"
                    :scroll="{ x: 'max-content' }"
                    :locale="{ emptyText }"
                    @change="handleTableChange"
                    @expand="onExpand"
                >
                    <template slot="provider" slot-scope="text, record">
                        <div class="dpm-cell">
                            <span>
                                {{ record.name }}
                                <a-tag v-if="requestId && record.linked" color="blue" class="dpm-tag">Já na minha base</a-tag>
                            </span>
                            <span class="dpm-cell-sub">{{ record.cnpj ? $options.filters.cnpj(record.cnpj) : "--" }}</span>
                        </div>
                    </template>
                    <template slot="place" slot-scope="text, record">
                        {{ [record.city, record.state].filter(Boolean).join(" / ") || "--" }}
                    </template>
                    <template slot="date" slot-scope="text">{{ formatDate(text) }}</template>
                    <template slot="actions" slot-scope="text, record">
                        <ActionMenu :actions="getActions(record)" />
                    </template>

                    <template slot="expandedRowRender" slot-scope="record">
                        <a-table
                            size="small"
                            row-key="id"
                            :columns="productColumns"
                            :data-source="productState(record).data"
                            :loading="productState(record).loading"
                            :pagination="productState(record).pagination"
                            :locale="{ emptyText: 'Nenhum produto encontrado.' }"
                            @change="(p) => loadProducts(record, p.current)"
                        >
                            <template slot="date" slot-scope="text">{{ formatDate(text) }}</template>
                        </a-table>
                    </template>
                </a-table>
            </div>

            <div class="dpm-foot">
                <template v-if="canLink && selectedRowKeys.length">
                    <span class="dpm-count">{{ selectedRowKeys.length }} selecionado(s)</span>
                    <a-button @click="selectedRowKeys = []">Limpar seleção</a-button>
                    <a-button
                        type="primary"
                        :loading="linking"
                        :disabled="selectedRowKeys.length > maxLink"
                        @click="link(selectedRowKeys)"
                    >
                        {{ linkLabel }}
                    </a-button>
                    <span v-if="selectedRowKeys.length > maxLink" class="dpm-warn">Selecione no máximo {{ maxLink }}.</span>
                </template>
                <a-button class="dpm-close" @click="$emit('close')">Fechar</a-button>
            </div>
        </div>
    </a-modal>
</template>

<script>
import moment from "moment";
import ProviderService from "@/services/v3/provider.service";
import ProductService from "@/services/v3/products/product.service";
import ErrorHandlerService from "@/services/ErrorHandlerService";
import ActionMenu from "@/components/popovers/action-menu.popover.vue";
import { perm_mixin } from "@/mixins/permission.mixin";

// Ordenações aceitas pelo GET clients/providers/discover (só um sentido cada).
const SORTS = {
    covered: "-covered_products",
    offers: "-offers_count",
    sales: "-sales_count",
    last_offer: "-last_offer_at",
    name: "name",
};
const DEFAULT_SORT = SORTS.covered;
const emptyFilters = () => ({ search: "", state: undefined, city: "", categories: [], products: [] });

export default {
    name: "DiscoverProvidersV2Modal",
    mixins: [perm_mixin],
    components: { ActionMenu },
    props: {
        visible: { type: Boolean, default: true },
        requestId: { type: [Number, String], default: null },
        requestLabel: { type: String, default: "" },
    },
    data() {
        return {
            svc: new ProviderService(),
            productSvc: new ProductService(),
            maxLink: 100,
            loading: false,
            linking: false,
            providers: [],
            states: [],
            categories: [],
            productOptions: [],
            searchingProducts: false,
            filters: emptyFilters(),
            appliedFilters: emptyFilters(),
            sort: DEFAULT_SORT,
            selectedRowKeys: [],
            products: {},
            feedback: null,
            productTimeout: null,
            pagination: {
                current: 1,
                pageSize: 20,
                total: 0,
                showSizeChanger: false,
                showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} fornecedores`,
            },
        };
    },
    computed: {
        title() {
            return this.requestId ? `Buscar fornecedores para a cotação #${this.requestId}` : "Buscar fornecedores";
        },
        linkLabel() {
            return this.requestId ? "Incluir na cotação" : "Adicionar à minha base";
        },
        canLink() {
            return this.can("CLIENT_SELLER", "UPDATE");
        },
        emptyText() {
            return this.requestId
                ? "Nenhum fornecedor novo encontrado para os produtos desta cotação."
                : "Nenhum fornecedor novo encontrado para os seus produtos.";
        },
        rowSelection() {
            return {
                selectedRowKeys: this.selectedRowKeys,
                onChange: (keys) => {
                    this.selectedRowKeys = keys;
                },
            };
        },
        columns() {
            const sortable = (key, dir = "descend") => ({
                sorter: true,
                sortDirections: [dir],
                defaultSortOrder: SORTS[key] === DEFAULT_SORT ? "descend" : undefined,
            });
            const cols = [
                { title: "Fornecedor", key: "name", scopedSlots: { customRender: "provider" }, ...sortable("name", "ascend") },
                { title: "Cidade/UF", key: "place", scopedSlots: { customRender: "place" } },
                {
                    title: this.requestId ? "Produtos da cotação" : "Meus produtos",
                    dataIndex: "covered_products_count",
                    key: "covered",
                    align: "right",
                    ...sortable("covered"),
                },
                { title: "Ofertas", dataIndex: "offers_count", key: "offers", align: "right", ...sortable("offers") },
                { title: "Vendas", dataIndex: "sales_count", key: "sales", align: "right", ...sortable("sales") },
                {
                    title: "Última oferta",
                    dataIndex: "last_offer_at",
                    key: "last_offer",
                    align: "center",
                    scopedSlots: { customRender: "date" },
                    ...sortable("last_offer"),
                },
            ];
            if (this.canLink) {
                cols.push({ key: "actions", fixed: "right", width: 50, scopedSlots: { customRender: "actions" } });
            }
            return cols;
        },
        productColumns() {
            return [
                { title: "Código", dataIndex: "ean", key: "ean", customRender: (t) => t || "--" },
                { title: "Produto", dataIndex: "name", key: "name" },
                { title: "Ofertas", dataIndex: "offers_count", key: "offers_count", align: "right" },
                { title: "Vendas", dataIndex: "sales_count", key: "sales_count", align: "right" },
                {
                    title: "Última oferta",
                    dataIndex: "last_offer_at",
                    key: "last_offer_at",
                    align: "center",
                    scopedSlots: { customRender: "date" },
                },
            ];
        },
    },
    created() {
        this.loadPage(1);
        this.svc
            .States()
            .then(({ data }) => {
                this.states = (data || []).map((s) => ({ value: s.est_sigla, label: s.est_nome }));
            })
            .catch((error) => ErrorHandlerService.handle(error, this.$store));
        this.productSvc
            .categories()
            .then(({ data }) => {
                const list = Array.isArray(data) ? data : data.data || [];
                this.categories = list.map((c) => ({ value: c.id, label: c.name }));
            })
            .catch((error) => ErrorHandlerService.handle(error, this.$store));
    },
    beforeDestroy() {
        clearTimeout(this.productTimeout);
    },
    methods: {
        filterOption(input, option) {
            // Com :options o antd monta as opções sem filhos de texto; cai no label das props.
            const children = option.componentOptions.children || [];
            const label = (children[0] && children[0].text) || option.componentOptions.propsData.title || "";
            return String(label).toLowerCase().includes(input.toLowerCase());
        },
        filtrar() {
            this.appliedFilters = { ...this.filters };
            this.loadPage(1);
        },
        limparFiltros() {
            this.filters = emptyFilters();
            this.filtrar();
        },
        formatDate(date) {
            return date ? moment(date, "YYYY-MM-DD HH:mm:ss").format("DD/MM/YYYY") : "--";
        },
        searchProducts(term) {
            clearTimeout(this.productTimeout);
            if (!term || term.trim().length < 2) return;
            this.productTimeout = setTimeout(() => {
                this.searchingProducts = true;
                this.productSvc
                    .query({ "filter[search]": term.trim(), per_page: 20 })
                    .then(({ data }) => {
                        this.productOptions = data.data || [];
                    })
                    .catch((error) => ErrorHandlerService.handle(error, this.$store))
                    .finally(() => {
                        this.searchingProducts = false;
                    });
            }, 400);
        },
        params(page) {
            const f = this.appliedFilters;
            const list = (values) => (values && values.length ? values.join(",") : undefined);
            return {
                page,
                per_page: this.pagination.pageSize,
                sort: this.sort,
                "filter[search]": (f.search || "").trim() || undefined,
                "filter[state]": f.state || undefined,
                "filter[city]": (f.city || "").trim() || undefined,
                "filter[category_id]": list(f.categories),
                "filter[product_ids]": list(f.products.map((p) => p.key)),
                "filter[request_id]": this.requestId || undefined,
            };
        },
        handleTableChange(pagination, filters, sorter) {
            this.sort = (sorter && sorter.order && SORTS[sorter.columnKey]) || DEFAULT_SORT;
            this.loadPage(pagination.current);
        },
        loadPage(page) {
            this.loading = true;
            this.svc
                .discover(this.params(page))
                .then(({ data }) => {
                    this.providers = data.data || [];
                    this.pagination.total = (data.meta && data.meta.total) || 0;
                    this.pagination.current = page;
                    this.products = {};
                })
                .catch((error) => {
                    this.providers = [];
                    this.pagination.total = 0;
                    ErrorHandlerService.handle(error, this.$store);
                    this.feedback = { type: "error", message: this.errorMessage(
                        error,
                        "Não foi possível buscar fornecedores.",
                        "Você não tem permissão para consultar fornecedores."
                    ) };
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        productState(record) {
            return this.products[record.id] || { data: [], loading: true, pagination: false };
        },
        onExpand(expanded, record) {
            if (expanded && !this.products[record.id]) this.loadProducts(record, 1);
        },
        loadProducts(record, page) {
            const prev = this.products[record.id];
            this.$set(this.products, record.id, {
                data: prev ? prev.data : [],
                loading: true,
                pagination: prev ? prev.pagination : false,
            });
            this.svc
                .discoverProducts(record.id, { page, per_page: 10, "filter[request_id]": this.requestId || undefined })
                .then(({ data }) => {
                    const total = (data.meta && data.meta.total) || 0;
                    this.$set(this.products, record.id, {
                        data: data.data || [],
                        loading: false,
                        pagination: total > 10 ? { current: page, pageSize: 10, total, size: "small" } : false,
                    });
                })
                .catch((error) => {
                    ErrorHandlerService.handle(error, this.$store);
                    this.$set(this.products, record.id, { data: [], loading: false, pagination: false });
                });
        },
        getActions(record) {
            return [
                {
                    label: this.linkLabel,
                    icon: this.requestId ? "playlist_add" : "add_business",
                    iconClass: "action-primary",
                    onClick: () => this.link([record.id]),
                },
            ];
        },
        errorMessage(error, fallback, forbidden) {
            const response = error && error.response;
            if (!response) return fallback;
            if (response.status === 403) return forbidden;
            if (response.status === 404) return "Cotação não encontrada.";
            const errors = (response.data && response.data.errors) || {};
            if (response.status === 422 && errors.request_id) return [].concat(errors.request_id)[0];
            return (response.data && response.data.message) || fallback;
        },
        link(providerIds) {
            if (!providerIds.length || this.linking) return;
            this.linking = true;
            this.feedback = null;
            const names = {};
            this.providers.forEach((p) => {
                names[p.id] = p.name;
            });
            this.svc
                .link(providerIds, this.requestId)
                .then(({ data }) => {
                    const parts = [`${data.linked} fornecedor(es) adicionado(s) à sua base`];
                    if (this.requestId) parts.push(`${data.included} incluído(s) na cotação`);
                    const skipped = data.skipped_provider_ids || [];
                    this.feedback = {
                        type: skipped.length ? "warning" : "success",
                        message: `${parts.join(" e ")}.`,
                        description: skipped.length
                            ? `Não incluído(s) na cotação por estar(em) bloqueado(s) para cotações na homologação: ${skipped
                                  .map((id) => names[id] || `#${id}`)
                                  .join(", ")}.`
                            : undefined,
                    };
                    this.selectedRowKeys = [];
                    this.$emit("linked", data);
                    this.loadPage(1);
                })
                .catch((error) => {
                    ErrorHandlerService.handle(error, this.$store);
                    this.feedback = { type: "error", message: this.errorMessage(
                        error,
                        "Não foi possível adicionar os fornecedores.",
                        "Você não tem permissão para adicionar fornecedores à sua base."
                    ) };
                })
                .finally(() => {
                    this.linking = false;
                });
        },
    },
};
</script>

<style lang="scss" scoped>
.dpm-modal {
    max-height: 88vh;
    background: #fff;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}
.dpm-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 18px 20px 14px;

    h3 {
        font-size: 16px;
        font-weight: 600;
        color: #262626;
    }
}
.dpm-sub {
    font-size: 13px;
    color: #8c8c8c;
    margin-top: 2px;
}
.dpm-x.ant-btn,
.dpm-x.ant-btn:focus,
.dpm-x.ant-btn:active {
    width: 28px;
    height: 28px;
    padding: 0;
    border: none;
    background: none;
    border-radius: 6px;
    color: #8c8c8c;
    box-shadow: none;
    display: flex;
    align-items: center;
    justify-content: center;
}
.dpm-x.ant-btn:hover {
    background: #f0f0f0;
    color: #262626;
}
.dpm-tools {
    padding: 0 20px;
}
.actions-col {
    display: flex;
    align-items: flex-end;
}
.actions-right {
    margin-left: auto;
    display: flex;
    gap: 8px;
}
.item-button {
    vertical-align: bottom;
}
::v-deep .fixed-height-select.ant-select,
::v-deep .fixed-height-select .ant-select-selection--multiple {
    min-height: 32px !important;
    max-height: 32px !important;
    overflow: auto;
}
.dpm-alert {
    margin: 0 20px 8px;
}
.dpm-body {
    flex: 1;
    overflow-y: auto;
    padding: 4px 20px 12px;
    border-top: 1px solid #f0f0f0;
    min-height: 240px;
}
.dpm-cell {
    display: flex;
    flex-direction: column;
}
.dpm-cell-sub {
    font-size: 0.85em;
    color: rgba(0, 0, 0, 0.45);
}
.dpm-tag {
    margin-left: 6px;
}
.dpm-foot {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    padding: 14px 20px;
    border-top: 1px solid #f0f0f0;
}
.dpm-count {
    font-weight: 600;
}
.dpm-warn {
    color: #cf1322;
}
.dpm-close {
    margin-left: auto;
}
</style>

<style lang="scss">
.dpm-wrap {
    .ant-modal {
        max-width: 95vw;
    }

    .ant-modal-content {
        padding: 0;
        border-radius: 10px;
        overflow: hidden;
    }

    .ant-modal-body {
        padding: 0;
    }
}
</style>
